import { useEffect, useState, useCallback, useMemo } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { DatePicker } from "../components/ui/date-picker";
import {
  Plus,
  Pencil,
  Trash2,
  Trophy,
  MapPin,
  Calendar as CalendarIcon,
  ArrowLeft,
  Bed,
  Wallet,
  Utensils,
  Hotel,
  Users,
  CheckCircle2,
  Circle,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { downloadTournamentLogisticsPdf } from "../components/tournaments/tournamentLogisticsPdf";
import PayNowButton from "../components/payments/PayNowButton";

const emptyTournament = {
  name: "",
  start_date: "",
  end_date: "",
  location: "",
  organizer: "",
  description: "",
  fee_per_athlete: "",
  parent_meal_price: "",
  hotel_name: "",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function TournamentsPage() {
  const { activeTeam, season, canEdit } = useTeams();
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyTournament);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    if (!activeTeam) return;
    const { data } = await api.get(`/teams/${activeTeam.id}/tournaments`, {
      params: season ? { season } : {},
    });
    setItems(data);
  }, [activeTeam, season]);

  useEffect(() => { load(); }, [load]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  const submit = async () => {
    if (!form.name.trim() || !form.start_date) {
      toast.error("Nome e data di inizio richiesti");
      return;
    }
    const payload = {
      ...form,
      team_id: activeTeam.id,
      season: season || null,
      fee_per_athlete: form.fee_per_athlete === "" ? null : Number(form.fee_per_athlete),
      parent_meal_price: form.parent_meal_price === "" ? null : Number(form.parent_meal_price),
      end_date: form.end_date || null,
    };
    if (editingId) await api.put(`/tournaments/${editingId}`, payload);
    else await api.post(`/tournaments`, payload);
    toast.success("Torneo salvato");
    setOpen(false);
    setForm(emptyTournament);
    setEditingId(null);
    load();
  };

  const handleNew = () => { setForm(emptyTournament); setEditingId(null); setOpen(true); };
  const handleEdit = (t) => {
    setForm({
      name: t.name || "",
      start_date: t.start_date || "",
      end_date: t.end_date || "",
      location: t.location || "",
      organizer: t.organizer || "",
      description: t.description || "",
      fee_per_athlete: t.fee_per_athlete ?? "",
      parent_meal_price: t.parent_meal_price ?? "",
      hotel_name: t.hotel_name || "",
    });
    setEditingId(t.id);
    setOpen(true);
  };
  const handleDelete = async (t) => {
    if (!window.confirm(`Eliminare il torneo "${t.name}"? Verranno rimosse anche camere, quote e pasti collegati.`)) return;
    await api.delete(`/tournaments/${t.id}`);
    if (selectedId === t.id) setSelectedId(null);
    toast.success("Torneo eliminato");
    load();
  };

  if (selectedId) {
    return (
      <TournamentDetail
        tournamentId={selectedId}
        onBack={() => setSelectedId(null)}
        canEdit={canEdit}
        team={activeTeam}
        onEdit={(t) => handleEdit(t)}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="tournaments-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Gestione</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">
            Tornei
          </h1>
          <p className="text-slate-500">
            {items.length} torneo{items.length === 1 ? "" : "i"} in stagione {season || "—"}
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={handleNew}
            className="rounded-full bg-sky-500 hover:bg-sky-600"
            data-testid="new-tournament-btn"
          >
            <Plus size={16} className="mr-1" /> Nuovo torneo
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t) => (
          <Card
            key={t.id}
            className="p-5 border-slate-200 hover:border-sky-300 transition cursor-pointer group"
            data-testid={`tournament-${t.id}`}
            onClick={() => setSelectedId(t.id)}
          >
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <Trophy size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold truncate">{t.name}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                  <CalendarIcon size={11} />
                  {formatDate(t.start_date)}
                  {t.end_date ? ` → ${formatDate(t.end_date)}` : ""}
                </div>
              </div>
              {canEdit && (
                <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition">
                  <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); handleEdit(t); }}>
                    <Pencil size={14} />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDelete(t); }}>
                    <Trash2 size={14} className="text-red-500" />
                  </Button>
                </div>
              )}
            </div>
            <div className="mt-3 space-y-1 text-xs text-slate-600">
              {t.location && (
                <div className="flex items-center gap-1.5">
                  <MapPin size={11} className="text-slate-400" /> {t.location}
                </div>
              )}
              {t.hotel_name && (
                <div className="flex items-center gap-1.5">
                  <Hotel size={11} className="text-slate-400" /> {t.hotel_name}
                </div>
              )}
              {(t.fee_per_athlete != null || t.parent_meal_price != null) && (
                <div className="flex items-center gap-3 pt-1.5 text-[11px]">
                  {t.fee_per_athlete != null && (
                    <span className="text-slate-500">
                      Quota: <strong className="text-slate-800">€ {Number(t.fee_per_athlete).toFixed(2)}</strong>
                    </span>
                  )}
                  {t.parent_meal_price != null && (
                    <span className="text-slate-500">
                      Pasto: <strong className="text-slate-800">€ {Number(t.parent_meal_price).toFixed(2)}</strong>
                    </span>
                  )}
                </div>
              )}
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="p-10 col-span-full text-center text-slate-500 border-dashed">
            <Trophy size={28} className="mx-auto text-slate-300 mb-2" />
            Nessun torneo registrato per la stagione {season || "—"}.
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica torneo" : "Nuovo torneo"}</DialogTitle>
            <DialogDescription className="sr-only">
              Inserisci i dati del torneo: date, luogo, hotel e quote.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nome torneo</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Torneo di Pasqua"
                data-testid="tournament-name"
              />
            </div>
            <div>
              <Label>Data inizio</Label>
              <DatePicker
                value={form.start_date}
                onChange={(v) => setForm({ ...form, start_date: v })}
                data-testid="tournament-start-date"
              />
            </div>
            <div>
              <Label>Data fine</Label>
              <DatePicker
                value={form.end_date}
                onChange={(v) => setForm({ ...form, end_date: v })}
                data-testid="tournament-end-date"
              />
            </div>
            <div>
              <Label>Luogo</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Rimini" />
            </div>
            <div>
              <Label>Organizzatore</Label>
              <Input value={form.organizer} onChange={(e) => setForm({ ...form, organizer: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Hotel</Label>
              <Input value={form.hotel_name} onChange={(e) => setForm({ ...form, hotel_name: e.target.value })} placeholder="Es. Hotel Sport, via..." />
            </div>
            <div>
              <Label>Quota atleta (€)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={form.fee_per_athlete}
                onChange={(e) => setForm({ ...form, fee_per_athlete: e.target.value })}
                placeholder="120.00"
              />
            </div>
            <div>
              <Label>Prezzo pasto genitore (€)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={form.parent_meal_price}
                onChange={(e) => setForm({ ...form, parent_meal_price: e.target.value })}
                placeholder="25.00"
              />
            </div>
            <div className="col-span-2">
              <Label>Descrizione / note</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} className="bg-sky-500 hover:bg-sky-600" data-testid="save-tournament-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Detail with tabs: Info / Rooms / Athlete Fees / Parent Meals --------
function TournamentDetail({ tournamentId, onBack, canEdit, team, onEdit }) {
  const teamId = team.id;
  const [detail, setDetail] = useState(null);
  const [players, setPlayers] = useState([]);
  const [parentsList, setParentsList] = useState([]);

  const load = useCallback(async () => {
    const [d, p, g] = await Promise.all([
      api.get(`/tournaments/${tournamentId}/detail`),
      api.get(`/teams/${teamId}/players`),
      api.get(`/teams/${teamId}/parents`).catch(() => ({ data: [] })),
    ]);
    setDetail(d.data);
    setPlayers(p.data);
    setParentsList(g.data);
  }, [tournamentId, teamId]);

  useEffect(() => { load(); }, [load]);

  if (!detail) return <p className="text-slate-500">Caricamento...</p>;

  const t = detail.tournament;
  const playerMap = Object.fromEntries(players.map((p) => [p.id, p]));

  return (
    <div className="space-y-5" data-testid="tournament-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="tournament-back-btn">
          <ArrowLeft size={16} className="mr-1" /> Tornei
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-3xl font-bold truncate">{t.name}</h1>
          <p className="text-xs text-slate-500">
            {formatDate(t.start_date)}
            {t.end_date ? ` → ${formatDate(t.end_date)}` : ""}
            {t.location ? ` · ${t.location}` : ""}
            {t.hotel_name ? ` · ${t.hotel_name}` : ""}
          </p>
        </div>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={() => onEdit(t)}>
            <Pencil size={14} className="mr-1" /> Modifica
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            try {
              await downloadTournamentLogisticsPdf({
                tournament: t,
                rooms: detail.rooms,
                fees: detail.fees,
                meals: detail.meals,
                players,
                team,
              });
              toast.success("PDF generato");
            } catch (e) {
              toast.error("Errore generazione PDF");
            }
          }}
          data-testid="logistics-pdf-btn"
        >
          <FileDown size={14} className="mr-1" /> Stampa logistica
        </Button>
      </div>

      <Tabs defaultValue="rooms">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="rooms" data-testid="tab-rooms"><Bed size={14} className="mr-1" />Camere</TabsTrigger>
          <TabsTrigger value="fees" data-testid="tab-fees"><Wallet size={14} className="mr-1" />Quote atleti</TabsTrigger>
          <TabsTrigger value="meals" data-testid="tab-meals"><Utensils size={14} className="mr-1" />Pranzi genitori</TabsTrigger>
        </TabsList>

        <TabsContent value="rooms" className="mt-4">
          <RoomsTab
            tournament={t}
            rooms={detail.rooms}
            players={players}
            playerMap={playerMap}
            canEdit={canEdit}
            reload={load}
          />
        </TabsContent>
        <TabsContent value="fees" className="mt-4">
          <FeesTab
            tournament={t}
            fees={detail.fees}
            players={players}
            playerMap={playerMap}
            canEdit={canEdit}
            reload={load}
          />
        </TabsContent>
        <TabsContent value="meals" className="mt-4">
          <MealsTab
            tournament={t}
            meals={detail.meals}
            parentsList={parentsList}
            playerMap={playerMap}
            canEdit={canEdit}
            reload={load}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// -------- Rooms tab --------
function RoomsTab({ tournament, rooms, players, playerMap, canEdit, reload }) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", capacity: 2, notes: "", player_ids: [] });

  const assigned = useMemo(() => {
    const s = new Set();
    rooms.forEach((r) => (r.player_ids || []).forEach((id) => s.add(id)));
    return s;
  }, [rooms]);

  const newRoom = () => {
    setForm({ name: "", capacity: 2, notes: "", player_ids: [] });
    setEditingId(null);
    setOpen(true);
  };
  const editRoom = (r) => {
    setForm({
      name: r.name || "",
      capacity: r.capacity || 2,
      notes: r.notes || "",
      player_ids: r.player_ids || [],
    });
    setEditingId(r.id);
    setOpen(true);
  };
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Nome camera richiesto");
    const payload = { ...form, tournament_id: tournament.id, capacity: Number(form.capacity) || 1 };
    if (editingId) await api.put(`/tournament-rooms/${editingId}`, payload);
    else await api.post(`/tournaments/${tournament.id}/rooms`, payload);
    setOpen(false);
    toast.success("Camera salvata");
    reload();
  };
  const remove = async (r) => {
    if (!window.confirm(`Eliminare ${r.name}?`)) return;
    await api.delete(`/tournament-rooms/${r.id}`);
    reload();
  };
  const togglePlayer = (pid) => {
    setForm((f) => {
      const has = f.player_ids.includes(pid);
      if (has) return { ...f, player_ids: f.player_ids.filter((x) => x !== pid) };
      if (f.player_ids.length >= f.capacity) {
        toast.error(`Capacità massima: ${f.capacity}`);
        return f;
      }
      return { ...f, player_ids: [...f.player_ids, pid] };
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {rooms.length} camere · {assigned.size}/{players.length} atleti assegnati
        </p>
        {canEdit && (
          <Button size="sm" onClick={newRoom} className="bg-sky-500 hover:bg-sky-600" data-testid="new-room-btn">
            <Plus size={14} className="mr-1" /> Nuova camera
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rooms.map((r) => {
          const ps = (r.player_ids || []).map((id) => playerMap[id]).filter(Boolean);
          const isFull = ps.length >= (r.capacity || 0);
          return (
            <Card key={r.id} className="p-4 border-slate-200" data-testid={`room-${r.id}`}>
              <div className="flex items-start gap-2">
                <div className="h-9 w-9 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center shrink-0">
                  <Bed size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold truncate">{r.name}</div>
                  <div className="text-xs text-slate-500">
                    {ps.length}/{r.capacity} posti {isFull && <span className="text-emerald-600">· completa</span>}
                  </div>
                </div>
                {canEdit && (
                  <div className="flex gap-0.5">
                    <Button size="icon" variant="ghost" onClick={() => editRoom(r)}><Pencil size={13} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(r)}><Trash2 size={13} className="text-red-500" /></Button>
                  </div>
                )}
              </div>
              {ps.length > 0 && (
                <div className="mt-2 space-y-1 text-xs">
                  {ps.map((p) => (
                    <div key={p.id} className="flex items-center gap-1.5 text-slate-700">
                      <Users size={11} className="text-slate-400" /> {p.last_name} {p.first_name}
                    </div>
                  ))}
                </div>
              )}
              {r.notes && <p className="mt-2 text-[11px] text-slate-500">{r.notes}</p>}
            </Card>
          );
        })}
        {rooms.length === 0 && (
          <Card className="p-8 col-span-full text-center text-slate-500 border-dashed text-sm">
            Nessuna camera assegnata.
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica camera" : "Nuova camera"}</DialogTitle>
            <DialogDescription className="sr-only">Assegna atleti a una camera dell'hotel.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome / Numero</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Camera 101" data-testid="room-name" />
              </div>
              <div>
                <Label>Capacità</Label>
                <Input type="number" min="1" max="20" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Atleti</Label>
              <div className="mt-1 max-h-56 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1">
                {players.map((p) => {
                  const inThis = form.player_ids.includes(p.id);
                  const inOther = assigned.has(p.id) && !inThis;
                  return (
                    <label key={p.id} className={`flex items-center gap-2 text-sm rounded p-1 ${inOther ? "opacity-40" : "cursor-pointer hover:bg-slate-50"}`}>
                      <input
                        type="checkbox"
                        checked={inThis}
                        disabled={inOther}
                        onChange={() => togglePlayer(p.id)}
                      />
                      <span>{p.last_name} {p.first_name}</span>
                      {inOther && <span className="text-[10px] text-slate-400 ml-auto">già in altra camera</span>}
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} className="bg-sky-500 hover:bg-sky-600" data-testid="save-room-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Fees tab --------
function FeesTab({ tournament, fees, players, playerMap, canEdit, reload }) {
  const [bulkAmount, setBulkAmount] = useState(tournament.fee_per_athlete ?? "");

  const feeByPlayer = useMemo(() => {
    const m = {};
    fees.forEach((f) => { m[f.player_id] = f; });
    return m;
  }, [fees]);

  const totals = useMemo(() => {
    const due = fees.reduce((s, f) => s + (Number(f.amount) || 0), 0);
    const paid = fees.filter((f) => f.paid).reduce((s, f) => s + (Number(f.amount) || 0), 0);
    return { due, paid, remaining: due - paid };
  }, [fees]);

  const bulkCreate = async () => {
    if (!bulkAmount || Number(bulkAmount) <= 0) return toast.error("Importo non valido");
    const missing = players.filter((p) => !feeByPlayer[p.id]);
    if (missing.length === 0) return toast.info("Tutte le quote già create");
    await Promise.all(missing.map((p) =>
      api.post(`/tournaments/${tournament.id}/fees`, {
        tournament_id: tournament.id,
        player_id: p.id,
        amount: Number(bulkAmount),
        paid: false,
      })
    ));
    toast.success(`${missing.length} quote create`);
    reload();
  };

  const togglePaid = async (f) => {
    await api.put(`/tournament-fees/${f.id}`, { ...f, paid: !f.paid });
    reload();
  };
  const updateAmount = async (f, amount) => {
    await api.put(`/tournament-fees/${f.id}`, { ...f, amount: Number(amount) || 0 });
    reload();
  };
  const remove = async (f) => {
    await api.delete(`/tournament-fees/${f.id}`);
    reload();
  };
  const addOne = async (p) => {
    if (!bulkAmount || Number(bulkAmount) <= 0) return toast.error("Imposta un importo");
    await api.post(`/tournaments/${tournament.id}/fees`, {
      tournament_id: tournament.id,
      player_id: p.id,
      amount: Number(bulkAmount),
      paid: false,
    });
    reload();
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 border-slate-200 flex flex-wrap items-end gap-3">
        <div>
          <Label>Importo quota standard</Label>
          <Input
            type="number" step="0.01" min="0"
            value={bulkAmount}
            onChange={(e) => setBulkAmount(e.target.value)}
            className="w-32"
            data-testid="bulk-fee-amount"
          />
        </div>
        {canEdit && (
          <Button onClick={bulkCreate} variant="outline" data-testid="bulk-fee-btn">
            <Plus size={14} className="mr-1" /> Genera per atleti mancanti
          </Button>
        )}
        <div className="ml-auto flex gap-4 text-sm">
          <div><span className="text-slate-500">Totale:</span> <strong>€ {totals.due.toFixed(2)}</strong></div>
          <div><span className="text-emerald-600">Incassato:</span> <strong className="text-emerald-700">€ {totals.paid.toFixed(2)}</strong></div>
          <div><span className="text-red-500">Da incassare:</span> <strong className="text-red-700">€ {totals.remaining.toFixed(2)}</strong></div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Atleta</th>
              <th className="text-right px-4 py-2">Quota</th>
              <th className="text-center px-4 py-2">Stato</th>
              {canEdit && <th className="px-4 py-2"></th>}
            </tr>
          </thead>
          <tbody>
            {players.map((p) => {
              const f = feeByPlayer[p.id];
              return (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-4 py-2">{p.last_name} {p.first_name}</td>
                  <td className="px-4 py-2 text-right">
                    {f ? (
                      canEdit ? (
                        <Input
                          type="number" step="0.01" min="0"
                          defaultValue={f.amount}
                          onBlur={(e) => {
                            const v = Number(e.target.value);
                            if (v !== Number(f.amount)) updateAmount(f, v);
                          }}
                          className="w-24 h-8 ml-auto text-right"
                        />
                      ) : (
                        <span>€ {Number(f.amount).toFixed(2)}</span>
                      )
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {f ? (
                      <button
                        type="button"
                        onClick={() => canEdit && togglePaid(f)}
                        disabled={!canEdit}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                          f.paid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                        }`}
                        data-testid={`fee-status-${p.id}`}
                      >
                        {f.paid ? <CheckCircle2 size={12} /> : <Circle size={12} />}
                        {f.paid ? "Pagato" : "Da pagare"}
                      </button>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {f && !f.paid && (
                        <PayNowButton kind="tournament_fee" itemId={f.id} label="Paga" testId={`pay-fee-${p.id}`} className="mr-1" />
                      )}
                      {f ? (
                        <Button size="icon" variant="ghost" onClick={() => remove(f)}>
                          <Trash2 size={13} className="text-red-500" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => addOne(p)}>
                          <Plus size={12} className="mr-1" />Aggiungi
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {players.length === 0 && (
              <tr><td className="px-4 py-6 text-center text-slate-400" colSpan={canEdit ? 4 : 3}>Nessun atleta in anagrafica</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// -------- Meals tab --------
function MealsTab({ tournament, meals, parentsList, playerMap, canEdit, reload }) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ parent_name: "", player_id: "", meals_count: 1, amount: tournament.parent_meal_price || 0, paid: false, notes: "" });

  const totals = useMemo(() => {
    const due = meals.reduce((s, m) => s + (Number(m.amount) || 0), 0);
    const paid = meals.filter((m) => m.paid).reduce((s, m) => s + (Number(m.amount) || 0), 0);
    const mealsTotal = meals.reduce((s, m) => s + (Number(m.meals_count) || 0), 0);
    return { due, paid, remaining: due - paid, mealsTotal };
  }, [meals]);

  const newMeal = () => {
    setForm({
      parent_name: "",
      player_id: "",
      meals_count: 1,
      amount: tournament.parent_meal_price || 0,
      paid: false,
      notes: "",
    });
    setEditingId(null);
    setOpen(true);
  };
  const editMeal = (m) => {
    setForm({
      parent_name: m.parent_name || "",
      player_id: m.player_id || "",
      meals_count: m.meals_count || 1,
      amount: m.amount || 0,
      paid: !!m.paid,
      notes: m.notes || "",
    });
    setEditingId(m.id);
    setOpen(true);
  };
  const submit = async () => {
    if (!form.parent_name.trim()) return toast.error("Nome genitore richiesto");
    const payload = {
      tournament_id: tournament.id,
      parent_name: form.parent_name,
      player_id: form.player_id || null,
      meals_count: Number(form.meals_count) || 1,
      amount: Number(form.amount) || 0,
      paid: !!form.paid,
      notes: form.notes,
    };
    if (editingId) await api.put(`/tournament-meals/${editingId}`, payload);
    else await api.post(`/tournaments/${tournament.id}/meals`, payload);
    setOpen(false);
    toast.success("Pasto salvato");
    reload();
  };
  const remove = async (m) => {
    if (!window.confirm(`Eliminare prenotazione di ${m.parent_name}?`)) return;
    await api.delete(`/tournament-meals/${m.id}`);
    reload();
  };
  const togglePaid = async (m) => {
    await api.put(`/tournament-meals/${m.id}`, { ...m, paid: !m.paid });
    reload();
  };
  const autoFillAmount = (count) => {
    if (tournament.parent_meal_price) {
      setForm((f) => ({ ...f, meals_count: count, amount: Number(count) * tournament.parent_meal_price }));
    } else {
      setForm((f) => ({ ...f, meals_count: count }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-4 text-sm">
          <div><span className="text-slate-500">Pasti:</span> <strong>{totals.mealsTotal}</strong></div>
          <div><span className="text-slate-500">Totale:</span> <strong>€ {totals.due.toFixed(2)}</strong></div>
          <div><span className="text-emerald-600">Incassato:</span> <strong className="text-emerald-700">€ {totals.paid.toFixed(2)}</strong></div>
          <div><span className="text-red-500">Da incassare:</span> <strong className="text-red-700">€ {totals.remaining.toFixed(2)}</strong></div>
        </div>
        {canEdit && (
          <Button size="sm" onClick={newMeal} className="bg-sky-500 hover:bg-sky-600" data-testid="new-meal-btn">
            <Plus size={14} className="mr-1" /> Nuova prenotazione
          </Button>
        )}
      </div>

      <Card className="p-0 overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Genitore</th>
              <th className="text-left px-4 py-2">Figlio</th>
              <th className="text-center px-4 py-2">Pasti</th>
              <th className="text-right px-4 py-2">Importo</th>
              <th className="text-center px-4 py-2">Stato</th>
              {canEdit && <th className="px-4 py-2"></th>}
            </tr>
          </thead>
          <tbody>
            {meals.map((m) => (
              <tr key={m.id} className="border-t border-slate-100" data-testid={`meal-${m.id}`}>
                <td className="px-4 py-2 font-medium">{m.parent_name}</td>
                <td className="px-4 py-2 text-slate-600">
                  {m.player_id && playerMap[m.player_id]
                    ? `${playerMap[m.player_id].last_name} ${playerMap[m.player_id].first_name}`
                    : "—"}
                </td>
                <td className="px-4 py-2 text-center">{m.meals_count}</td>
                <td className="px-4 py-2 text-right">€ {Number(m.amount).toFixed(2)}</td>
                <td className="px-4 py-2 text-center">
                  <button
                    type="button"
                    disabled={!canEdit}
                    onClick={() => canEdit && togglePaid(m)}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                      m.paid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {m.paid ? <CheckCircle2 size={12} /> : <Circle size={12} />}
                    {m.paid ? "Pagato" : "Da pagare"}
                  </button>
                </td>
                {canEdit && (
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {!m.paid && <PayNowButton kind="tournament_meal" itemId={m.id} label="Paga" testId={`pay-meal-${m.id}`} className="mr-1" />}
                    <Button size="icon" variant="ghost" onClick={() => editMeal(m)}><Pencil size={13} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(m)}><Trash2 size={13} className="text-red-500" /></Button>
                  </td>
                )}
              </tr>
            ))}
            {meals.length === 0 && (
              <tr><td className="px-4 py-6 text-center text-slate-400" colSpan={canEdit ? 6 : 5}>Nessuna prenotazione</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica prenotazione" : "Nuova prenotazione pasto"}</DialogTitle>
            <DialogDescription className="sr-only">Registra una prenotazione pasto di un genitore.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Genitore</Label>
              <Input
                list="parents-suggestions"
                value={form.parent_name}
                onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
                placeholder="Nome e cognome"
                data-testid="meal-parent-name"
              />
              <datalist id="parents-suggestions">
                {parentsList.map((g) => (
                  <option key={g.id} value={`${g.first_name} ${g.last_name}`} />
                ))}
              </datalist>
            </div>
            <div>
              <Label>Figlio (opzionale)</Label>
              <select
                value={form.player_id}
                onChange={(e) => setForm({ ...form, player_id: e.target.value })}
                className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm"
              >
                <option value="">—</option>
                {Object.values(playerMap).map((p) => (
                  <option key={p.id} value={p.id}>{p.last_name} {p.first_name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Pasti</Label>
                <Input
                  type="number" min="1"
                  value={form.meals_count}
                  onChange={(e) => autoFillAmount(e.target.value)}
                />
              </div>
              <div>
                <Label>Importo (€)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.paid} onChange={(e) => setForm({ ...form, paid: e.target.checked })} />
              <span>Già pagato</span>
            </label>
            <div>
              <Label>Note</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} className="bg-sky-500 hover:bg-sky-600" data-testid="save-meal-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
